import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import DataTableCard from '../../Components/Table/DataTableCard';

const BatchList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [batch, setBatch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null); // { message: '', type: 'success' }

  useEffect(() => {
    if (location.state?.toastMessage) {
      setToast({
        message: location.state.toastMessage,
        type: location.state.toastType || 'success',
      });
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');

  // Fetch program options
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const data = await api.getAllProgramOptions();
        setPrograms(data || []);
      } catch (err) {
        console.error('Error fetching program options:', err);
      }
    };
    fetchPrograms();
  }, []);

  const getBatch = async (programId) => {
    try {
      setLoading(true);
      const data = await api.getBatch({ programId });
      setBatch(data);
    } catch (err) {
      console.error('Error fetching batch:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const data = await api.getBatch({ programId: selectedProgramId });
      setBatch(data);
    } catch (err) {
      console.error('Error refreshing batch:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    getBatch(selectedProgramId);
  }, [selectedProgramId]);

  return (
    <div className='container-fluid p-0'>
      <DataTableCard
        icon='bi-calendar-week'
        title='Daftar Batch'
        subtitle='Pantau periode dan status pembukaan tiap batch program.'
        actions={
          <>
            <div className='d-flex align-items-center'>
              <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>
                Program:
              </label>
              <select
                className='form-select form-select-sm rounded-pill shadow-sm border-0 bg-light px-3'
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                style={{ minWidth: '180px', fontSize: '0.8rem' }}
              >
                <option value=''>Semua Program</option>
                {programs.map((prog) => (
                  <option key={prog.id} value={prog.id}>
                    {prog.label || prog.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => navigate(`/batch/add`)}
              className='btn btn-primary rounded-pill px-3'
            >
              <i className='bi bi-plus-circle me-2'></i>Tambah
            </button>
          </>
        }
      >
        {!loading && batch.length === 0 ? (
          <div className='text-center py-5'>
            <div className='text-muted mb-3 opacity-25'>
              <i className='bi bi-calendar-x fs-1'></i>
            </div>
            <h6 className='fw-bold text-dark mb-1'>
              Belum ada data batch yang tersedia
            </h6>
            <p className='text-muted small mb-4'>
              Tidak ditemukan data batch. Coba muat ulang untuk memperbarui.
            </p>
            <button
              className='btn btn-outline-primary px-4 rounded-pill'
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                  Memuat...
                </>
              ) : (
                <>
                  <i className='bi bi-arrow-clockwise me-2'></i>
                  Muat Ulang
                </>
              )}
            </button>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-hover align-middle mb-0'>
              <thead className='bg-light text-muted small text-uppercase fw-bold'>
                <tr>
                  <th className='ps-4 py-3 border-0'>Batch</th>
                  <th className='border-0 py-3'>Program</th>
                  <th className='border-0 py-3'>Periode</th>
                  <th className='border-0 py-3'>Status</th>
                  <th className='border-0 py-3 text-end pe-4'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className='placeholder-glow'>
                      <td className='ps-4 py-3'><span className='placeholder col-6 rounded'></span></td>
                      <td className='py-3'><span className='placeholder col-8 rounded'></span></td>
                      <td className='py-3'><span className='placeholder col-10 rounded'></span></td>
                      <td className='py-3'><span className='placeholder col-6 rounded'></span></td>
                      <td className='text-end pe-4 py-3'><span className='placeholder col-4 rounded'></span></td>
                    </tr>
                  ))
                ) : (
                  batch.map((value) => (
                    <tr
                      key={value.id}
                      className='cursor-pointer transition-all'
                      onClick={() => navigate(`/batch/${value.id}`)}
                    >
                      <td className='ps-4 py-3 fw-bold text-dark'>
                        Batch {value.batch}
                      </td>
                      <td className='py-3'>
                        <span className='small text-muted'>{value.programName}</span>
                      </td>
                      <td className='py-3'>
                        <span className='extra-small fw-medium text-muted bg-light px-2 py-1 rounded'>
                          <i className='bi bi-calendar-event me-1'></i>
                          {value.dateStart} <i className='bi bi-arrow-right mx-1'></i> {value.dateEnd}
                        </span>
                      </td>
                      <td className='py-3'>
                        <div className='d-flex align-items-center gap-2'>
                          <span
                            className={`badge bg-${value.statusOffered ? 'success' : 'secondary'} bg-opacity-10 text-${value.statusOffered ? 'success' : 'secondary'} rounded-pill px-3 py-2`}
                          >
                            {value.statusOffered ? 'Running' : 'Closed'}
                          </span>
                          {value.isOpen ? (
                            <span className='text-success extra-small fw-semibold'>
                              <i className='bi bi-unlock-fill me-1'></i>Open
                            </span>
                          ) : (
                            <span className='text-secondary extra-small fw-semibold'>
                              <i className='bi bi-lock-fill me-1'></i>Not Open
                            </span>
                          )}
                        </div>
                      </td>
                      <td className='py-3 text-end pe-4'>
                        <button
                          className='btn btn-outline-primary btn-sm rounded-circle'
                          style={{ width: '32px', height: '32px' }}
                          title='Detail'
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/batch/${value.id}`);
                          }}
                        >
                          <i className='bi bi-eye-fill'></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </DataTableCard>

      {/* Bootstrap Toast Notification Container */}
      {toast && (
        <div
          className='position-fixed bottom-0 end-0 p-3'
          style={{ zIndex: 1060 }}
        >
          <div
            className={`toast show align-items-center text-white bg-${toast.type === 'danger' ? 'danger' : 'success'} border-0 shadow-lg`}
            role='alert'
            aria-live='assertive'
            aria-atomic='true'
          >
            <div className='d-flex'>
              <div className='toast-body'>
                <i
                  className={`bi ${toast.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2`}
                ></i>
                {toast.message}
              </div>
              <button
                type='button'
                className='btn-close btn-close-white me-2 m-auto'
                onClick={() => setToast(null)}
                aria-label='Close'
              ></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchList;
